import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Alert } from "@mui/material";
import axiosInstance from "../axiosInstance";
import LOGO_BASE64 from "../assets/data/logoBase64";

const primaryColor = "#1976d2";
const successColor = "#2e7d32";
const errorColor = "#d32f2f";

const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

const formatCurrency = (amount) =>
  Math.round(Number(amount || 0)).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

const formatSignedCurrency = (amount) =>
  `${amount < 0 ? "- " : ""}₹${formatCurrency(Math.abs(amount))}`;

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "N/A";

const buildReceiptHTML = (data) => {
  const {
    transaction,
    customerDetails,
    accountBalanceData,
    employerName,
    calculatedData = {},
    branchDetails,
    employerDetails,
  } = data;

  const sales = transaction.sales || [];
  const trays = transaction.trays || [];
  const paymentAmount = Number(transaction.paymentAmount) || 0;
  const discount = Number(transaction.discount) || 0;
  const damageRefunds =
    Array.isArray(transaction.damageRefunds) &&
    transaction.damageRefunds.length > 0
      ? transaction.damageRefunds
      : transaction.damageRefund?.enabled || transaction.damageRefund?.total
      ? [transaction.damageRefund]
      : [];
  const damageRefundTotal = damageRefunds.reduce(
    (sum, refund) => sum + (Number(refund.total) || 0),
    0
  );

  const getSaleTotal = (sale) => {
    const savedTotal = Number(sale?.total);
    return sale?.total != null && Number.isFinite(savedTotal)
      ? savedTotal
      : (Number(sale?.numberOfItems) || 0) * (Number(sale?.costPerItem) || 0);
  };
  const salesTotal = sales.reduce((sum, sale) => sum + getSaleTotal(sale), 0);
  const currentLabourTotal = transaction.labourCharges?.enabled
    ? Number(transaction.labourCharges.total || 0)
    : 0;
  const trayPaymentTotal = Number(calculatedData.trayPaymentTotal) || 0;
  const totalCredits = paymentAmount + trayPaymentTotal + damageRefundTotal;
  const netSalesTotal = salesTotal + currentLabourTotal - discount;

  const accountBalance =
    accountBalanceData?.totalDueAmount !== undefined
      ? accountBalanceData.totalDueAmount
      : transaction?.accountBalance || 0;
  const openingBalance =
    accountBalance - (salesTotal + currentLabourTotal - discount - totalCredits);

  const title = employerName || employerDetails?.employerName || "Durga Bhavani Fruits";

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${escapeHtml(customerDetails?.name)} - ${formatDate(transaction.dateOfTransaction)}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 16px; font-size: 13px; line-height: 1.45; color: #1e293b; background: #f1f5f9; }
        .receipt-container { border: 1px solid #cbd5e1; border-radius: 4px; padding: 18px; max-width: 520px; margin: auto; background: #fff; box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08); }
        .receipt-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding-bottom: 12px; border-bottom: 2px solid ${primaryColor}; }
        .brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .brand img { width: 42px; height: 42px; object-fit: contain; }
        .company-name { color: ${primaryColor}; font-size: 17px; font-weight: 700; }
        .branch-name { color: #64748b; margin-top: 3px; }
        .receipt-date { color: #475569; text-align: right; white-space: nowrap; }
        .customer { padding: 14px 0; border-bottom: 1px solid #cbd5e1; }
        .customer-name { font-size: 17px; font-weight: 700; text-transform: capitalize; }
        .customer-location { color: #64748b; margin-top: 3px; text-transform: capitalize; }
        .calculation { margin-top: 12px; padding: 12px; background: #f8fafc; border: 1px solid #bfdbfe; border-radius: 4px; }
        .calculation-title { margin-bottom: 7px; color: ${primaryColor}; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .calculation-row { display: grid; grid-template-columns: 20px minmax(0, 1fr) auto; align-items: baseline; gap: 6px; padding: 6px 0; border-bottom: 1px solid #e2e8f0; }
        .calculation-operator { color: #64748b; text-align: center; font-weight: 700; }
        .calculation-operator.add { color: ${successColor}; }
        .calculation-operator.subtract { color: ${errorColor}; }
        .calculation-value { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .calculation-result { margin: 6px -12px -12px; padding: 10px 12px; border-top: 2px solid ${primaryColor}; border-bottom: 0; background: #eaf3ff; color: #0f172a; font-weight: 700; }
        .calculation-result .calculation-operator { color: ${primaryColor}; }
        .ledger-section { padding: 12px 0; border-bottom: 1px solid #cbd5e1; }
        .section-title { color: ${primaryColor}; font-size: 11px; font-weight: 700; text-transform: uppercase; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #dbeafe; }
        .entry { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 6px 0; border-bottom: 1px dashed #e2e8f0; }
        .entry-label { flex: 1; min-width: 0; overflow-wrap: anywhere; }
        .entry-detail { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .entry-total { margin-top: 8px; padding-top: 8px; border-top: 1px solid #94a3b8; border-bottom: 0; font-weight: 700; }
        .summary { padding-top: 12px; }
        .positive { color: ${successColor}; }
        .negative { color: ${errorColor}; }
        .proprietor-meta { font-size: 11px; color: #64748b; margin-top: 3px; }
        @media (max-width: 480px) {
          body { padding: 8px; font-size: 12px; }
          .receipt-container { padding: 12px; }
          .receipt-header { align-items: flex-start; gap: 8px; }
          .brand { gap: 7px; }
          .brand img { width: 34px; height: 34px; }
          .company-name { font-size: 15px; overflow-wrap: anywhere; }
          .branch-name, .receipt-date { font-size: 11px; }
          .proprietor-meta { font-size: 10px; }
          .customer-name { font-size: 16px; }
          .entry, .calculation-row { gap: 6px; }
          .entry-detail { white-space: normal; overflow-wrap: anywhere; max-width: 58%; }
          .calculation { padding: 10px; }
          .calculation-value { max-width: 56%; white-space: normal; overflow-wrap: anywhere; }
          .calculation-result { margin: 6px -10px -10px; padding: 10px; }
        }
        @media print {
          body { padding: 0; background: #fff; }
          .receipt-container { border: none; border-radius: 0; box-shadow: none; max-width: none; padding: 8px; }
          .ledger-section, .customer, .receipt-header { break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="receipt-container">
        <div class="receipt-header">
          <div class="brand">
            <img src="${LOGO_BASE64}" />
            <div>
              <div class="company-name">${escapeHtml(title)}</div>
              <div class="branch-name">${escapeHtml(branchDetails?.name || "Transaction Receipt")}</div>
              <div class="proprietor-meta">
                Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
              </div>
            </div>
          </div>
          <div class="receipt-date">Date<br /><strong>${formatDate(transaction.dateOfTransaction)}</strong></div>
        </div>

        <div class="customer">
          <div class="customer-name">${escapeHtml(customerDetails?.name || "N/A")}</div>
          <div class="customer-location">${escapeHtml(customerDetails?.location || "N/A")}</div>
        </div>

        ${sales.length > 0 || currentLabourTotal > 0 || discount > 0 ? `
        <section class="ledger-section">
          <div class="section-title">Sales</div>
          ${sales.map((s) => `
            <div class="entry">
              <span class="entry-label">${escapeHtml(s.itemName || "Item")}</span>
              <span class="entry-detail">${s.numberOfItems} × ₹${formatCurrency(s.costPerItem)} = ₹${formatCurrency(getSaleTotal(s))}</span>
            </div>`).join("")}
          ${currentLabourTotal > 0 ? `
            <div class="entry">
              <span class="entry-label">Loading Charges</span>
              <span class="entry-detail">${transaction.labourCharges.numberOfItems} × ₹${formatCurrency(transaction.labourCharges.costPerItem)} = ₹${formatCurrency(currentLabourTotal)}</span>
            </div>` : ""}
          ${discount > 0 ? `
            <div class="entry negative">
              <span class="entry-label">Discount</span>
              <span class="entry-detail">- ₹${formatCurrency(discount)}</span>
            </div>` : ""}
          <div class="entry entry-total">
            <span>Net Sales After Discount</span>
            <span>₹${formatCurrency(netSalesTotal)}</span>
          </div>
        </section>` : ""}

        ${paymentAmount > 0 || trays.length > 0 || damageRefundTotal > 0 ? `
        <section class="ledger-section">
          <div class="section-title">Payments and Tray Returns</div>
          ${paymentAmount > 0 ? `
            <div class="entry positive">
              <span class="entry-label">Paid Amount</span>
              <span class="entry-detail">₹${formatCurrency(paymentAmount)}</span>
            </div>` : ""}
          ${trays.map((t) => {
            const price = Number(t.costPerTray) || 0;
            const rowTotal = t.excludePayment ? 0 : (Number(t.numberOfTrays) || 0) * price;
            return `
            <div class="entry positive">
              <span class="entry-label">Tray Returned${t.trayName ? ` (${escapeHtml(t.trayName)})` : ""}${t.excludePayment ? " (Payment Excluded)" : ""}</span>
              <span class="entry-detail">${t.numberOfTrays} × ₹${formatCurrency(price)} = ₹${formatCurrency(rowTotal)}</span>
            </div>`;
          }).join("")}
          ${damageRefunds.map((refund) => `
            <div class="entry positive">
              <span class="entry-label">Damage Refund${refund.itemName ? ` (${escapeHtml(refund.itemName)})` : ""}</span>
              <span class="entry-detail">₹${formatCurrency(refund.total)}</span>
            </div>`).join("")}
          <div class="entry entry-total positive">
            <span>Total Credits</span>
            <span>₹${formatCurrency(totalCredits)}</span>
          </div>
        </section>` : ""}

        <section class="summary">
          <div class="calculation">
            <div class="calculation-title">Balance Calculation</div>
            <div class="calculation-row">
              <span class="calculation-operator"></span>
              <span>Opening Balance</span>
              <span class="calculation-value">${formatSignedCurrency(openingBalance)}</span>
            </div>
            <div class="calculation-row">
              <span class="calculation-operator add">+</span>
              <span>Sales and Loading</span>
              <span class="calculation-value">₹${formatCurrency(salesTotal + currentLabourTotal)}</span>
            </div>
            ${discount > 0 ? `<div class="calculation-row"><span class="calculation-operator subtract">−</span><span>Discount</span><span class="calculation-value">₹${formatCurrency(discount)}</span></div>` : ""}
            <div class="calculation-row">
              <span class="calculation-operator subtract">−</span>
              <span>Payments, Returns and Refunds</span>
              <span class="calculation-value">₹${formatCurrency(totalCredits)}</span>
            </div>
            <div class="calculation-row calculation-result">
              <span class="calculation-operator">=</span>
              <span>Balance After Transaction</span>
              <span class="calculation-value">${formatSignedCurrency(accountBalance)}</span>
            </div>
          </div>
        </section>
      </div>
    </body>
    </html>
  `;
};

const ReceiptViewer = () => {
  const { transactionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      try {
        const response = await axiosInstance.get(`/pdf/transaction/${transactionId}`);
        setData(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.statusText ||
            err.message ||
            "Failed to load transaction details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (transactionId) {
      fetchTransactionDetails();
    }
  }, [transactionId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data?.transaction) {
    return (
      <Box p={3}>
        <Alert severity="warning">No transaction data found</Alert>
      </Box>
    );
  }

  return (
    <iframe
      title="Transaction Receipt"
      srcDoc={buildReceiptHTML(data)}
      style={{ width: "100%", height: "100vh", border: 0, display: "block" }}
    />
  );
};

export default ReceiptViewer;
